<template>
  <div class="mt-3" style="width: 90%;">
    <template v-if="ready">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="input-group w-auto">
          <span class="input-group-text">Buscar</span>
          <input
            v-model="q"
            @input="applySearch"
            type="text"
            class="form-control"
            placeholder="Crédito, juzgado, estatus, expediente…"
          />
        </div>

        <button @click="handleImprimir" class="btn btn-primary" style="color: white;">
          Generar Excel <i class="fa-solid fa-download"></i>
        </button>
      </div>

      <div class="d-flex justify-content-center align-items-center text-center">
        <div style="overflow-x: auto; width: 100%;">
          <table class="table table-striped table-hover text-center align-middle" id="procesosTable">
            <thead>
              <tr>
                <th>#</th>
                <th>Cliente</th>
                <th>Presentado</th>
                <th>Juzgado</th>
                <th>Estatus</th>
                <th>No Expediente</th>
                <th>Notif.</th>
                <th>Acuerdo</th>
                <th>Demanda</th>
                <th>Observaciones</th>
                <th>No Préstamo</th>
              </tr>
            </thead>
            <!-- ⚠️ Sin <tbody>: lo pinta DataTables -->
          </table>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="text-center my-5">
        <div class="spinner-border text-warning" role="status">
          <span class="visually-hidden">Cargando…</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import Security from "../security";
import DataTable from "datatables.net-dt"; // DataTables sin jQuery
import ExcelJS from "exceljs";
// En main.js: import 'datatables.net-dt/css/dataTables.dataTables.min.css';

export default {
  name: "TablaProcesos",
  props: { estatus: { type: String, default: "Todos" } },
  data() {
    return {
      ready: false,
      procesos: [],
      q: "",
      dt: null,
      tableEl: null,
      tableClickHandler: null,
      expandedIds: new Set(), // para cambiar el ícono up/down
    };
  },
  watch: {
    estatus: {
      immediate: true,
      async handler() {
        await this.fetchProcesos();
        this.upsertDataTable();
      },
    },
  },
  beforeUnmount() {
    if (this.tableEl && this.tableClickHandler) {
      this.tableEl.removeEventListener("click", this.tableClickHandler);
      this.tableClickHandler = null;
    }
    if (this.dt) {
      this.dt.destroy();
      this.dt = null;
    }
  },
  methods: {
    async fetchProcesos() {
      this.ready = false;
      this.expandedIds.clear();
      try {
        const body = { estatus: this.estatus === "Todos" ? null : this.estatus };
        const res = await fetch(
          process.env.VUE_APP_API_URL + "/admin/estado-legal/list",
          Security.requestOptions(body, "POST")
        );
        const json = await res.json();
        if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`);
        // Espera objetos con: id, cliente, fechaPresentado, juzgado, estatus,
        // noExpediente, fechaNotificacion, fechaAcuerdo, continuaProcesoDemanda,
        // observaciones, noPrestamo
        this.procesos = json.data?.procesos || [];
        this.ready = true;
      } catch (err) {
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: err.message,
          showConfirmButton: false,
          timer: 1800,
        });
      }
    },

    // 👉 función para acortar texto con “…” (respetando palabras)
    shortenText(text, max = 40) {
      if (!text) return "—";
      const s = String(text).trim();
      if (s.length <= max) return s;
      // busca el último espacio antes de max para no cortar palabra a la mitad
      const cutAt = s.lastIndexOf(" ", max - 1);
      const head = cutAt > 20 ? s.slice(0, cutAt) : s.slice(0, max); // si no hay espacio cercano, corta duro
      return head + "…";
    },

    // 👉 escapado básico para evitar HTML en observaciones
    escapeHtml(s) {
      return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },

    upsertDataTable() {
      const el = document.getElementById("procesosTable");
      if (!el) return;

      const data = this.procesos.map((p, idx) => ({ __rownum: idx + 1, ...p }));

      if (!this.dt) {
        this.tableEl = el;
        this.dt = new DataTable(this.tableEl, {
          data,
          deferRender: true,
          paging: true,
          searching: true, // buscador con dt.search()
          info: true,
          ordering: true,
          pageLength: 10,
          language: { url: "https://cdn.datatables.net/plug-ins/1.13.5/i18n/es-ES.json" },
          columns: [
            { data: "__rownum", width: "40px" },                                 // 0
            { data: "cliente", defaultContent: "—" },                             // 1
            { data: "fechaPresentado", render: (d) => this.fmt(d) },              // 2
            { data: "juzgado", defaultContent: "—" },                             // 3
            {
              data: "estatus",                                                   // 4
              render: (d) =>
                `<span class="badge bg-secondary">${this.escapeHtml(d ?? "—")}</span>`,
            },
            { data: "noExpediente", defaultContent: "—" },                        // 5  👈 NUEVA COLUMNA
            { data: "fechaNotificacion", render: (d) => this.fmt(d) },           // 6
            { data: "fechaAcuerdo", render: (d) => this.fmt(d) },                // 7
            { data: "continuaProcesoDemanda", render: (d) => (d ? "Sí" : "No") },// 8
            // 👇 OBSERVACIONES: muestra SIEMPRE versión corta con “…”. El completo va en row.child().
            {
              data: "observaciones",                                             // 9
              orderable: false,
              className: "col-observaciones",
              render: (d, _type, row) => {
                const id = String(row.id);
                const isOpen = this.expandedIds.has(id);
                const full = this.escapeHtml(d ?? "—");
                const short = this.escapeHtml(this.shortenText(d, 40));
                return `
                  <div class="d-flex">
                    <div class="observacion-text" title="${full}">
                      ${short}
                    </div>
                    ${
                      d
                        ? `<button class="btn btn-link p-0 ms-2 obs-toggle" data-id="${id}" aria-expanded="${isOpen ? "true" : "false"}" title="Ver observación completa">
                             <i class="fa-regular ${isOpen ? "fa-square-caret-up" : "fa-square-caret-down"} text-primary"></i>
                           </button>`
                        : ""
                    }
                  </div>
                `;
              },
            },
            { data: "noPrestamo", defaultContent: "—" },                          // 10
          ],
          columnDefs: [
            { targets: "_all", defaultContent: "" },
            { targets: 9, width: "320px" }, // 👉 Observaciones (nuevo índice)
          ],
        });

        // Toggle detalle
        this.tableClickHandler = (ev) => {
          const btn = ev.target.closest?.(".obs-toggle");
          if (!btn) return;

          const tr = btn.closest("tr");
          const row = this.dt.row(tr);
          if (!row) return;

          const rowData = row.data();
          const id = String(rowData.id || btn.dataset.id);

          if (row.child.isShown()) {
            row.child.hide();
            this.expandedIds.delete(id);
            btn.setAttribute("aria-expanded", "false");
          } else {
            row.child(this.buildChildHtml(rowData)).show();
            this.expandedIds.add(id);
            btn.setAttribute("aria-expanded", "true");
          }
          row.invalidate().draw(false);
        };

        this.tableEl.addEventListener("click", this.tableClickHandler);
        this.applySearch();
        return;
      }

      // refresco de datos
      this.dt.clear();
      this.dt.rows.add(data);
      this.dt.draw(false);
      this.applySearch();
    },

    applySearch() {
      if (this.dt) this.dt.search(this.q || "").draw();
    },

    buildChildHtml(rowData) {
      const full = this.escapeHtml(rowData.observaciones || "");
      return `
        <div class="obs-child text-end">
          <div class="observacion-expandida">
            <p class="m-0">${full}</p>
          </div>
        </div>
      `;
    },

    fmt(param) {
      if (!param) return "—";
      const date = new Date(param);
      if (isNaN(date)) return "—";
      const dd = ("0" + date.getUTCDate()).slice(-2);
      const mm = ("0" + (date.getUTCMonth() + 1)).slice(-2);
      const yy = date.getUTCFullYear();
      return `${dd}/${mm}/${yy}`;
    },

    async handleImprimir() {
      if (!this.dt) return;
      const rows = this.dt.rows({ search: "applied" }).data().toArray();

      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet("Procesos");
      ws.columns = [
        { header: "#", key: "id", width: 6 },
        { header: "Cliente", key: "cliente", width: 25 },
        { header: "Presentado", key: "fechaPresentado", width: 15 },
        { header: "Juzgado", key: "juzgado", width: 25 },
        { header: "Estatus", key: "estatus", width: 15 },
        { header: "No Expediente", key: "noExpediente", width: 22 }, // 👈 NUEVA COLUMNA
        { header: "Notif.", key: "fechaNotificacion", width: 15 },
        { header: "Acuerdo", key: "fechaAcuerdo", width: 15 },
        { header: "Demanda", key: "continuaProcesoDemanda", width: 10 },
        { header: "Observaciones", key: "observaciones", width: 40 },
        { header: "No Préstamo", key: "noPrestamo", width: 20 },
      ];
      ws.getRow(1).font = { bold: true };

      const data = rows.map((p, i) => ({
        id: i + 1,
        cliente: p.cliente || "—",
        fechaPresentado: this.fmt(p.fechaPresentado),
        juzgado: p.juzgado || "—",
        estatus: p.estatus || "—",
        noExpediente: p.noExpediente || "", // 👈 exporta expediente
        fechaNotificacion: this.fmt(p.fechaNotificacion),
        fechaAcuerdo: this.fmt(p.fechaAcuerdo),
        continuaProcesoDemanda: p.continuaProcesoDemanda ? "Sí" : "No",
        observaciones: p.observaciones || "",
        noPrestamo: p.noPrestamo || "",
      }));
      ws.addRows(data);

      ws.addTable({
        name: "ProcesosTable",
        ref: "A1",
        headerRow: true,
        style: { theme: "TableStyleMedium2", showRowStripes: true },
        columns: ws.columns.map((c) => ({ name: c.header, filterButton: true })),
        rows: data.map((r) => ws.columns.map((c) => r[c.key])),
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Procesos-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.table { min-width: 1100px; }
td { vertical-align: middle; }

/* ✅ SOLO Observaciones: ancho y truncado visual del contenedor */
td.col-observaciones {
  width: 320px;
  max-width: 320px;
  overflow: hidden;
}
td.col-observaciones .observacion-text {
  display: block;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: justify;
}

/* Detalle (row.child) alineado a la derecha y legible */
.observacion-expandida {
  display: inline-block;
  max-width: 95%;
  background-color: #f8f9fa;
  border-left: 3px solid #0d6efd;
  padding: .75rem 1rem;
  border-radius: 4px;
  text-align: right;
  white-space: normal;
  word-break: break-word;
}
</style>
